import React, { useRef, useState } from 'react';
import { api, PROBLEM_AREAS, PRIORITIES } from '../api/client';
import { compressImage, humanSize } from '../api/images';
import { useSpeechToText } from '../api/speech';

export default function CreateTicket({ onCreated, onCancel }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [area, setArea] = useState(0);          // default CCTV
  const [priority, setPriority] = useState(1);  // default Medium
  const [pics, setPics] = useState([]);         // { file, preview, size }
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  // Voice-to-text: append recognised speech to the description.
  const speech = useSpeechToText({
    onResult: (text) =>
      setDescription((prev) => (prev ? `${prev} ${text}` : text)),
  });

  async function addFiles(fileList) {
    const incoming = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (incoming.length === 0) return;
    setStatus('Compressing…');
    const processed = [];
    for (const f of incoming) {
      const c = await compressImage(f);
      processed.push({ file: c, preview: URL.createObjectURL(c), size: c.size });
    }
    setPics((prev) => [...prev, ...processed]);
    setStatus('');
  }

  function removePic(i) {
    setPics((prev) => {
      URL.revokeObjectURL(prev[i].preview);
      return prev.filter((_, idx) => idx !== i);
    });
  }

  async function submit() {
    setError('');
    if (!title.trim()) { setError('Please enter a short subject.'); return; }
    setBusy(true);
    try {
      setStatus('Creating ticket…');
      const ticket = await api.createTicket({
        title: title.trim(),
        description: description.trim() || null,
        problemArea: area,
        priority,
      });

      if (pics.length > 0) {
        setStatus(`Uploading ${pics.length} photo${pics.length > 1 ? 's' : ''}…`);
        await api.uploadImages(ticket.id, pics.map((p) => p.file));
      }
      setStatus('');
      onCreated(ticket.id);
    } catch (e) {
      setError(e.message);
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>← Back</button>
        <h1 style={{ fontSize: 20 }}>New ticket</h1>
      </div>

      <div className="card">
        <div className="field">
          <label className="label">Subject *</label>
          <input className="input" placeholder="e.g. Camera 3 offline at gate"
            value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} />
        </div>

        <div className="field">
          <label className="label">Type</label>
          <div className="seg cols-2">
            {PROBLEM_AREAS.map((a) => (
              <button key={a.key} type="button"
                className={`seg-btn ${area === a.value ? 'active' : ''}`}
                onClick={() => setArea(a.value)}>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label className="label">Priority</label>
          <div className="seg">
            {PRIORITIES.map((p) => (
              <button key={p.value} type="button"
                className={`seg-btn ${p.cls} ${priority === p.value ? 'active' : ''}`}
                onClick={() => setPriority(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="label" style={{ marginBottom: 0 }}>Description</label>
            {speech.supported && (
              <button type="button"
                className={`mic-btn ${speech.listening ? 'rec' : ''}`}
                onClick={speech.toggle}
                aria-label={speech.listening ? 'Stop recording' : 'Speak description'}>
                {speech.listening ? '● Listening… tap to stop' : '🎤 Speak'}
              </button>
            )}
          </div>
          <textarea className="textarea" style={{ marginTop: 6 }} placeholder="Describe the issue, or tap 🎤 to speak…"
            value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
        </div>

        <div className="field">
          <label className="label">Photos <span className="faint">(auto-compressed)</span></label>
          <div className="thumbs">
            {pics.map((p, i) => (
              <div className="thumb" key={i}>
                <img src={p.preview} alt="" />
                <button className="x" onClick={() => removePic(i)}>×</button>
                <span className="sz">{humanSize(p.size)}</span>
              </div>
            ))}
            <div className="upload-tile" onClick={() => cameraRef.current?.click()}>
              <span className="ic">📷</span>
              Camera
            </div>
            <div className="upload-tile" onClick={() => galleryRef.current?.click()}>
              <span className="ic">🖼️</span>
              Gallery
            </div>
          </div>
          {/* capture="environment" opens the rear camera directly on phones */}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment"
            hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={galleryRef} type="file" accept="image/*" multiple
            hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>

        {status && <p className="ok-text">{status}</p>}
        {error && <p className="error-text">{error}</p>}

        <button className="btn btn-primary" disabled={busy} onClick={submit} style={{ marginTop: 6 }}>
          {busy ? <span className="spinner" /> : 'Submit ticket'}
        </button>
      </div>
    </div>
  );
}
